import React, { useEffect } from "react";
import { getVideoUrl, transformations } from "../../util/get-url-utils";
import PropTypes from "prop-types";
import {
  Type,
  Card,
  CardContent,
  CardMedia,
  Dialog,
  DialogTitle,
  DialogTitleActions,
  VideoPlayer,
} from "@ebscois/react-component-library";

import { StyledHeading, ItalicType } from "./_.styled";
import { useBaseMediaUrls } from "../../hooks/useBaseMediaUrls";
import ViewInContextString from "../ViewInContextString";
import { logVideoViewed, logVideoPlayed } from "../../util/analytics";

const DialogLightBoxVideo = ({
  videos,
  currentVideo,
  onNextVideo,
  onPrevVideo,
  isOpen,
  onRequestClose,
  affordance,
}) => {
  const cloudinaryUrl = useBaseMediaUrls("videos");

  useEffect(() => {
    const video = videos[currentVideo];
    if (isOpen && video) {
      logVideoViewed({
        title: video.title,
        mediaId: video.publicId,
        type: video.format,
        affordance,
      });
    }
  }, [isOpen, currentVideo]); // eslint-disable-line react-hooks/exhaustive-deps

  const onPlay = () => {
    const video = videos[currentVideo];
    logVideoPlayed({
      title: video.title,
      mediaId: video.publicId,
      type: video.format,
      affordance,
    });
  };

  return videos?.length ? (
    <>
      <Dialog
        isOpen={isOpen}
        maxWidth="lg"
        onRequestClose={onRequestClose}
        data-testid="modal-light-box-video-root"
      >
        <DialogTitle aria-live="polite">
          <Type
            element="h1"
            styledAs="p"
            fontSize="lg"
            textTransform="uppercase"
            data-testid="modal-light-box-video-counter"
          >
            Video {currentVideo + 1} of {videos.length}
          </Type>
          <DialogTitleActions
            disabledNext={videos.length === 1}
            disabledPrevious={currentVideo === 0}
            onNextClick={onNextVideo}
            onPreviousClick={onPrevVideo}
            onRequestClose={onRequestClose}
          />
        </DialogTitle>
        <Card borderColor="gray400" borderStyle="solid" borderWidth="1px">
          <CardMedia
            iconBackground="gray100"
            iconTransparency={0}
            borderYColor="gray400"
            borderYStyle="solid"
            borderYWidth="1px"
            data-element="media-video-card"
            showFullscreenButton={false}
            data-testid="modal-light-box-video-media"
          >
            <VideoPlayer
              inheritSize
              autoPlay={false}
              lazyLoad={false}
              src={getVideoUrl(
                videos?.[currentVideo]?.publicId,
                cloudinaryUrl,
                transformations.fullRes
              )}
              poster={`${getVideoUrl(
                videos?.[currentVideo]?.publicId,
                cloudinaryUrl,
                transformations.fullRes
              )}.jpg`}
              onPlayButtonClick={onPlay}
              data-testid="modal-light-box-video-player"
            />
          </CardMedia>
          <CardContent>
            <StyledHeading
              element="h4"
              fontWeight="semibold"
              data-testid="modal-light-box-video-caption"
            >
              {videos[currentVideo]?.title}
            </StyledHeading>
            {videos[currentVideo]?.description && (
              <Type
                fontSize="sm"
                element="p"
                data-testid="modal-light-box-video-description"
              >
                {videos[currentVideo]?.description}
              </Type>
            )}
            {videos[currentVideo]?.associatedTopics &&
              ViewInContextString({
                associatedTopics: videos[currentVideo].associatedTopics,
                onRequestClose,
              })}
            <ItalicType
              fontSize="xs"
              element="p"
              paddingTop="sm"
              data-testid="modal-light-box-video-copyright"
            >
              {videos[currentVideo]?.copyright}
            </ItalicType>
          </CardContent>
        </Card>
      </Dialog>
    </>
  ) : (
    <></>
  );
};

DialogLightBoxVideo.propTypes = {
  videos: PropTypes.arrayOf(
    PropTypes.shape({
      caption: PropTypes.string.isRequired,
      copyright: PropTypes.string.isRequired,
      mediaHost: PropTypes.string.isRequired,
      publicId: PropTypes.string.isRequired,
      topicTitle: PropTypes.string.isRequired,
      topicUrl: PropTypes.string.isRequired,
    })
  ),
  currentVideo: PropTypes.number.isRequired,
  onNextVideo: PropTypes.func.isRequired,
  onPrevVideo: PropTypes.func.isRequired,
};

DialogLightBoxVideo.displayName = "DialogLightBoxVideo";

export default DialogLightBoxVideo;
